import React, { useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useForm, FormProvider } from 'react-hook-form';
import { Cleaner, formatDeliveryValues, customFieldsWithSelectOptions, formatCustomField } from '#component/shipment/form/helper';
import { setShipment, processAfterSubmitAction, setFormStatus, setPristine } from '#action/shipment/form';
import { SET_SHIPMENT } from '#action/types';
import { fetchUsersByProject } from '#action/user/users-by-project';
import { reduxFormResolver } from '#helper/rhf-resolver';
import { useStepSubmit } from '#component/shipment/form/use-step-submit';
import propTypes from './prop-types';
import validate from './form/validate';
import Form from './form';
import { uuidv4 } from '#helper';

const generateDelivery = (states, beginDate, endDate) => {
  const insignificantStepKeys = ['id', 'done'];

  const newStates = states.map(({ formattedAddress, ...rest }) => ({ ...rest }));
  if (newStates[0]) {
    newStates[0].date = beginDate;
  } else {
    newStates.push({ date: beginDate, id: uuidv4() });
  }

  if (newStates.length >= 2) {
    const destination = newStates[newStates.length - 1];
    destination.date = endDate;

    if (endDate && !destination.timedelta) {
      destination.timedelta = 3600000;
    } else if (!endDate && destination.timedelta) {
      delete destination.timedelta;
    }
  } else if (endDate) {
    newStates.push({ date: endDate, id: uuidv4() });
  }

  const cleanNewStates = Cleaner.cleanArray(newStates)
    .filter(s => Object.keys(s).filter(k => !insignificantStepKeys.includes(k)).length);

  if (cleanNewStates.length !== states.length) {
    return generateDelivery(cleanNewStates, beginDate, endDate);
  }
  return cleanNewStates;
};

const handleFormSubmit = (id, dispatch, { states = [], uid, defaultDevices }) =>
  async values => {
    const {
      label,
      beginDate,
      endDate,
      project,
      customFields
    } = values;

    const cleanNewStates = generateDelivery(states, beginDate, endDate);

    const shipmentData = {
      devices: defaultDevices ?? [],
      label: label || '',
      projectId: project || undefined,
      uid
    };

    if (customFields?.length) {
      shipmentData.customFields = customFields.map(field => {
        const { id: fieldId, selectOptions, value, ...rest } = field;

        let formattedValue = value === '' || value == null ? undefined : value;
        if (
          (typeof formattedValue === 'number' || (typeof formattedValue === 'string' && /^\d+$/.test(formattedValue))) &&
          Array.isArray(selectOptions) &&
          selectOptions[Number(formattedValue)] !== undefined
        ) {
          formattedValue = String(selectOptions[Number(formattedValue)]);
        } else if (typeof formattedValue === 'number') {
          formattedValue = String(formattedValue);
        }

        return {
          ...rest,
          value: formattedValue
        };
      });
    }

    dispatch(setShipment(
      id,
      shipmentData,
      formatDeliveryValues(cleanNewStates)
    ));

    dispatch(fetchUsersByProject(project));

    await dispatch(processAfterSubmitAction());
    await dispatch(setFormStatus('succeeded'));
  };

const EditForm = props => {
  const {
    initialValues, onSubmit, editable, shipmentId, projectId, label, registerStepSubmit, form
  } = props;
  const dispatch = useDispatch();

  const methods = useForm({
    resolver: reduxFormResolver((values, context) => validate(values, { ...props, ...context })),
    defaultValues: initialValues || {},
    mode: 'all'
  });

  const { reset, formState: { isDirty } } = methods;
  const prevInitialRef = React.useRef(null);

  useEffect(() => {
    if (!initialValues) return;
    const serialized = JSON.stringify(initialValues);
    if (prevInitialRef.current === serialized) return;
    prevInitialRef.current = serialized;
    if (!isDirty) {
      reset(initialValues);
    }
  }, [initialValues, reset, isDirty]);

  useEffect(() => {
    if (form) {
      dispatch(setPristine(form, !isDirty));
    }
  }, [dispatch, form, isDirty]);

  const onFormSubmit = async values => {
    if (editable) {
      await onSubmit(values);
    } else {
      dispatch({ type: SET_SHIPMENT, payload: { id: shipmentId, projectId, label } });
      await dispatch(processAfterSubmitAction());
    }
  };

  useStepSubmit(registerStepSubmit, methods, onFormSubmit);

  return (
    <FormProvider {...methods}>
      <Form {...props} onFormSubmit={onFormSubmit} />
    </FormProvider>
  );
};

function mapStateToProps(state, props) {
  const {
    shipment: reduxShipment,
    shipmentForm,
    shipments,
    account,
    projectCustomFields: { customFields: projectCustomFields }
  } = state;
  const { dispatch, editable, resetProducts, formStatus, history } = props;
  const isCopiedShipment = history?.location?.search?.includes('copied') ?? false;

  const shipment = reduxShipment?.id ?
    reduxShipment : { ...reduxShipment, ...(shipmentForm.shipment || {}) };
  const {
    id,
    uid,
    devices,
    defaultDevices = [],
    projectId,
    project,
    states,
    label,
    customFields: shipmentCustomFields
  } = shipment;

  const initialValues = {
    label: label || '',
    project: projectId
  };

  // Custom fields initialization
  if (id && shipmentCustomFields && !isCopiedShipment) {
    const withSelectOptions =
      customFieldsWithSelectOptions(shipmentCustomFields, projectCustomFields);

    initialValues.customFields = withSelectOptions;
  }

  if (isCopiedShipment) {
    const withShipmentValues = projectCustomFields.map(projectCF => {
      const fieldInShipment = shipmentCustomFields?.find(shipmentCF =>
        shipmentCF.key === projectCF.key);

      if (fieldInShipment) {
        return fieldInShipment;
      }

      return formatCustomField([projectCF])[0];
    });

    const withSelectOptions =
      customFieldsWithSelectOptions(withShipmentValues, projectCustomFields);
    initialValues.customFields = withSelectOptions;
  }

  if (!id && !isCopiedShipment && projectCustomFields?.length) {
    initialValues.customFields = formatCustomField(projectCustomFields);
  }

  if (states?.[0]?.date) {
    const beginDate = new Date(states[0].date);
    beginDate.setSeconds(0, 0);
    initialValues.beginDate = beginDate.toISOString();
    let endDate = null;

    if (states.length > 1 && Date.parse(states[states.length - 1].date)) {
      endDate = new Date(states[states.length - 1].date);
      endDate.setSeconds(0, 0);
      endDate = endDate.toISOString();
    }
    initialValues.endDate = endDate;
  } else if (shipment.beginDate) {
    const beginDate = new Date(shipment.beginDate);
    beginDate.setSeconds(0, 0);
    initialValues.beginDate = beginDate.toISOString();
    initialValues.endDate = null;
  } else {
    const now = new Date();
    now.setSeconds(0, 0);
    initialValues.beginDate = now.toISOString();
    initialValues.endDate = null;
  }

  return {
    initialValues,
    uid,
    devices,
    project,
    projectId,
    label,
    defaultDevices,
    shipments,
    shipment,
    shipmentId: id,
    account,
    editable,
    formStatus,
    resetProducts,
    onSubmit: handleFormSubmit(id, dispatch, shipment)
  };
}

EditForm.propTypes = propTypes;

export default connect(mapStateToProps)(EditForm);
